import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { isLogin } from '../utils/auth';
import UserService from '../service/User';

// User 타입 정의
export interface User {
  userid: string;
  branchcode: number;
  department: string;
  position: string;
  name: string;
  email: string;
  tel: string;
  usertype: string;
  state: string;
  regdate: string;
  notification: string;
  login_type: string;
  img: string;
  marketing: string;
  info_consent: string;
  plan_grade: string | null;
  plan_region: string | null;
  plan_start: string | null;
  plan_end: string | null;
  register_device: string | null;
  last_login: string | null;
  recommend_code?: string | null;
  isCoupon: boolean;
  freeTrialSubscription: {
    startDate: string;
    endDate: string;
  };
}

// Context의 기본 값 타입
interface UserContextType {
  user: User | null;
  setUser: (user: User | null) => void;
  loading: boolean;
  refreshUser: () => Promise<void>;
}

// Context 생성
const UserContext = createContext<UserContextType | undefined>(undefined);

// Provider 컴포넌트
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(false);

  const refreshUser = useCallback(async () => {
    if (!isLogin()) {
      setUser(null);
      return;
    }
    setLoading(true);
    try {
      const res = await UserService.userInfo();
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUser = async () => {
    setLoading(true);
    try {
      const res = await UserService.userInfo();
      setUser(res.data);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isLogin()) {
      refreshUser();
    } else {
      setLoading(false);
    }
  }, []);

  // 로그인 상태인데 user가 null인 경우 자동 fetch
  useEffect(() => {
    if (isLogin() && !user && !loading) {
      refreshUser();
    }
    if (isLogin() && !user?.freeTrialSubscription) {
      refreshUser();
    }
  }, [user]);

  return (
    <UserContext.Provider value={{ user, setUser, loading, refreshUser }}>
      {children}
    </UserContext.Provider>
  );
};

// Custom Hook
export const useUser = (): UserContextType => {
  const context = useContext(UserContext);

  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
